import { getOwner } from '@ember/application';
import { type Registry as Services, service } from '@ember/service';
import type Transition from '@ember/routing/transition';
import BaseRoute from 'uplisting-frontend/pods/base/route';
import IndexRoute from 'uplisting-frontend/pods/index/route';
import { parseResponse } from 'ember-simple-auth/authenticators/oauth2-implicit-grant';
import { type ISessionTokenDecoded } from 'ember-simple-auth/services/session';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { AnalyticsEvents } from 'uplisting-frontend/services/analytics';
import {
  getGaClientId,
  getGclid,
} from 'uplisting-frontend/utils/google-analytics';

const NEW_ACCOUNT_WINDOW_MS = 5 * 60 * 1_000;

export default class AuthCallbackRoute extends BaseRoute {
  @service analytics!: Services['analytics'];
  @service sentry!: Services['sentry'];

  async beforeModel(transition: Transition) {
    await super.beforeModel(transition);

    try {
      await this.session.invalidate();

      const hash = parseResponse(window.location.hash);
      const invitationId = hash['state'];

      hash.idToken = hash.access_token;

      await this.session.authenticate('authenticator:auth-url-hash', hash);

      if (!this.session.isAuthenticated) {
        return;
      }

      const { jwtDecode } = await import('jwt-decode');

      const decode = jwtDecode<ISessionTokenDecoded>(hash.access_token ?? '');

      if (!decode) {
        return;
      }

      const user = this.store.createRecord('user', {
        email: decode.user.email,
        name: decode.user.fullName || decode.user.email,
        identities: [decode.sub],
        invitation: invitationId,
        gaClientId: getGaClientId(),
        gclid: getGclid(),
      });

      await user.save();

      this.session.setUser(user);

      if (this.isNewlyCreated(user)) {
        this.analytics.trackEvent(AnalyticsEvents.registrationCompleted, {
          invited: Boolean(invitationId),
        });
      } else {
        this.analytics.trackEvent(AnalyticsEvents.signedIn);
      }

      if (this.session.currentUser?.onboardRoute) {
        await this.router.transitionTo(this.session.currentUser.onboardRoute);
      } else {
        const indexRoute = getOwner(this)?.lookup('route:index') as IndexRoute;

        await indexRoute.redirectAfterLogin();
      }
    } catch (error) {
      this.sentry.captureException(error);

      this.session.handleNotAuthorized();
    }
  }

  private isNewlyCreated(user: ModelRegistry['user']): boolean {
    const createdAt = user.createdAt?.getTime();

    return (
      createdAt !== undefined && Date.now() - createdAt < NEW_ACCOUNT_WINDOW_MS
    );
  }
}
